import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { updateStelle, deleteStelle } from '../api';
import { useConfirm } from '../components/ConfirmDialog';
import { STATUS_CONFIG, STATUS_OPTIONEN, MONATE_DE, DOW_DE } from './AnalyticsConstants';
import { computeActivityFeed, computeSources, parseNotizen, serializeNotizen, newNote, formatNoteDate, formatGehalt } from './AnalyticsUtils';
import { BookmarkIcon, TrashIcon, PersonSVG, CalendarSVG, EuroSVG } from './AnalyticsIcons';
import { ExternModal, MiniCalendar } from './AnalyticsComponents';
import { C } from './AnalyticsTokens';

// ─── Inline date cell with floating picker ────────────────────────────────────
function DateCell({ value, onSave }) {
  const ref    = useRef(null);
  const [open, setOpen]   = useState(false);
  const [openUp, setOpenUp] = useState(false);

  const today    = new Date();
  const todayISO = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
  const [viewYear,  setViewYear]  = useState(() => parseInt((value || todayISO).slice(0,4), 10));
  const [viewMonth, setViewMonth] = useState(() => parseInt((value || todayISO).slice(5,7), 10) - 1);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const display = value
    ? new Date(value + 'T12:00:00').toLocaleDateString('de-DE', { day:'2-digit', month:'2-digit', year:'2-digit' })
    : '—';

  const firstDow    = new Date(viewYear, viewMonth, 1).getDay();
  const startOffset = firstDow === 0 ? 6 : firstDow - 1;
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const navBtn = {
    background: 'rgba(255,255,255,0.13)', border: '1px solid rgba(255,255,255,0.18)',
    cursor: 'pointer', width: 28, height: 28, borderRadius: 8,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  };

  return (
    <div ref={ref} style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
      <button
        onClick={() => {
          if (!open && ref.current) {
            const rect = ref.current.getBoundingClientRect();
            setOpenUp(window.innerHeight - rect.bottom < 310);
          }
          setOpen(v => !v);
        }}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: value ? 'var(--an-text-soft)' : 'var(--an-text-faint)',
          fontFamily: 'var(--an-font-body)', fontSize: 13,
          padding: '3px 7px', borderRadius: 6,
          transition: 'background 0.12s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.05)'}
        onMouseLeave={e => e.currentTarget.style.background = 'none'}
      >
        {display}
      </button>
      {open && (
        <div style={{
          position: 'absolute', left: 0, zIndex: 300, width: 230,
          ...(openUp ? { bottom: 'calc(100% + 4px)' } : { top: 'calc(100% + 4px)' }),
          background: C.surface, border: `1px solid ${C.borderMid}`, borderRadius: 14,
          overflow: 'hidden', boxShadow: '0 8px 28px rgba(0,0,0,0.14)',
        }}>
          {/* header */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 10px', background: C.teal }}>
            <button type="button" onClick={() => { if (viewMonth===0){setViewMonth(11);setViewYear(y=>y-1);}else setViewMonth(m=>m-1); }} style={navBtn}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M10 12l-4-4 4-4" stroke="rgba(255,255,255,0.9)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            <span style={{ fontSize:13, fontWeight:700, color:'#fff' }}>{MONATE_DE[viewMonth]} {viewYear}</span>
            <button type="button" onClick={() => { if (viewMonth===11){setViewMonth(0);setViewYear(y=>y+1);}else setViewMonth(m=>m+1); }} style={navBtn}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke="rgba(255,255,255,0.9)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </div>
          {/* dow */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', padding:'6px 6px 2px', background:'rgba(0,77,64,0.05)' }}>
            {DOW_DE.map(d => <div key={d} style={{ textAlign:'center', fontSize:9, fontWeight:700, color:C.textDim, letterSpacing:'0.08em' }}>{d}</div>)}
          </div>
          {/* days */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', padding:'2px 6px 6px', rowGap:2 }}>
            {cells.map((day, i) => {
              if (!day) return <div key={i} style={{ aspectRatio:'1' }} />;
              const iso   = `${viewYear}-${String(viewMonth+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
              const isSel = iso === value;
              const isToday = iso === todayISO;
              return (
                <button key={i} type="button" onClick={() => { onSave(iso); setOpen(false); }} style={{
                  aspectRatio:'1', width:'100%', border: isToday && !isSel ? `1.5px solid ${C.teal}` : '1.5px solid transparent',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:12, fontWeight: isSel ? 700 : isToday ? 600 : 400,
                  background: isSel ? C.amber : isToday ? 'rgba(0,77,64,0.08)' : 'transparent',
                  color: isSel ? '#3A2700' : isToday ? C.teal : C.text,
                  borderRadius:7, cursor:'pointer', fontFamily: C.body, boxSizing:'border-box',
                }}>{day}</button>
              );
            })}
          </div>
          {/* footer */}
          <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 10px 10px', borderTop:`1px solid ${C.border}` }}>
            <button type="button" onClick={() => { onSave(''); setOpen(false); }} style={{ background:'none', border:'none', cursor:'pointer', fontSize:12, color:C.textMuted, fontFamily:C.body }}>Leeren</button>
            <button type="button" onClick={() => { onSave(todayISO); setOpen(false); }} style={{ background:C.tealMid, border:`1px solid rgba(0,77,64,0.2)`, borderRadius:999, cursor:'pointer', fontSize:12, fontWeight:600, color:C.teal, fontFamily:C.body, padding:'4px 12px' }}>Heute</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Logo placeholder ─────────────────────────────────────────────────────────
const LOGO_COLORS = ['#004D40','#0284c7','#7C3AED','#D97706','#DC2626','#059669','#1B1B1B','#FF3F70'];
function LogoCell({ name }) {
  const initials = (name || '?').slice(0, 2).toUpperCase();
  const color = LOGO_COLORS[(name || '').charCodeAt(0) % LOGO_COLORS.length];
  return (
    <div className="an-tbl-logo" style={{ background: color }}>
      {initials}
    </div>
  );
}

// ─── Inline status dropdown ───────────────────────────────────────────────────
function StatusCell({ stelle, onStatusChange }) {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const ref = useRef(null);
  const cfg = STATUS_CONFIG[stelle.bewerbungs_status || 'unterlagen_angefertigt'] || STATUS_CONFIG['unterlagen_angefertigt'];

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const handleOpen = (e) => {
    e.stopPropagation();
    if (!open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setOpenUp(spaceBelow < 260);
    }
    setOpen(v => !v);
  };

  return (
    <div ref={ref} className="an-status-wrap">
      <button
        className="an-status-trigger"
        style={{ background: cfg.bg, color: cfg.color, borderColor: cfg.border }}
        onClick={handleOpen}
      >
        <span className="an-status-dot" style={{ background: cfg.color }} />
        {cfg.label}
        <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
          <path d="M3 4.5l3 3 3-3" stroke={cfg.color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && (
        <div className="an-status-menu" style={openUp ? { top: 'auto', bottom: 'calc(100% + 4px)' } : {}}>
          {STATUS_OPTIONEN.map(opt => {
            const oCfg = STATUS_CONFIG[opt.value] ?? STATUS_CONFIG[''];
            return (
              <button
                key={opt.value}
                className={`an-status-menu-item${stelle.bewerbungs_status === opt.value ? ' an-status-menu-item--active' : ''}`}
                onClick={e => { e.stopPropagation(); onStatusChange(stelle.id, opt.value); setOpen(false); }}
              >
                <span className="an-status-dot" style={{ background: oCfg.color }} />
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Inline detail expansion panel ───────────────────────────────────────────
function InlineDetail({ stelle, onSave, onLoeschen }) {
  const confirm = useConfirm();
  const [notizen,   setNotizen]   = useState(() => parseNotizen(stelle.notizen));
  const [neueNotiz, setNeueNotiz] = useState('');
  const status = stelle.bewerbungs_status || '';
  const showInterview = status === 'kennenlerngespraech' || !!stelle.interview_datum;

  useEffect(() => { setNotizen(parseNotizen(stelle.notizen)); }, [stelle.id]);

  const addNotiz = useCallback(() => {
    if (!neueNotiz.trim()) return;
    const updated = [...notizen, newNote(neueNotiz.trim())];
    setNotizen(updated);
    setNeueNotiz('');
    onSave({ notizen: serializeNotizen(updated) });
  }, [neueNotiz, notizen, onSave]);

  const removeNotiz = useCallback((id) => {
    const updated = notizen.filter(n => n.id !== id);
    setNotizen(updated);
    onSave({ notizen: serializeNotizen(updated) });
  }, [notizen, onSave]);

  const inputStyle = {
    width: '100%', padding: '8px 11px',
    border: `1.5px solid ${C.borderMid}`, borderRadius: 9,
    fontSize: 13, fontFamily: C.body, color: C.text,
    background: C.bg, outline: 'none', boxSizing: 'border-box',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  };
  const labelStyle = {
    fontSize: 10, fontWeight: 700, letterSpacing: '0.12em',
    textTransform: 'uppercase', color: C.textDim,
    marginBottom: 6, display: 'flex', alignItems: 'center', gap: 5,
  };
  const onFocus = e => { e.target.style.borderColor = C.amber; e.target.style.boxShadow = '0 0 0 3px rgba(255,188,0,0.14)'; };
  const onBlur  = e => { e.target.style.borderColor = C.borderMid; e.target.style.boxShadow = 'none'; };

  return (
    <div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1.4fr',
        gap: 20,
        padding: '20px 24px 20px',
        background: `linear-gradient(180deg, rgba(0,77,64,0.035) 0%, rgba(0,77,64,0.01) 100%)`,
        borderTop: `1px solid ${C.border}`,
      }}>

        {/* ── Col 1: Ansprechpartner + Termine ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <div style={labelStyle}><PersonSVG size={10} /> Ansprechpartner</div>
            <input
              type="text"
              defaultValue={stelle.empfaenger_ansprechperson || ''}
              placeholder="—"
              style={inputStyle}
              onFocus={onFocus}
              onBlur={e => {
                onBlur(e);
                if (e.target.value !== (stelle.empfaenger_ansprechperson || ''))
                  onSave({ empfaenger_ansprechperson: e.target.value });
              }}
              onKeyDown={e => e.key === 'Enter' && e.target.blur()}
            />
          </div>
          <div>
            <div style={labelStyle}><CalendarSVG size={10} /> Abgeschickt am</div>
            <MiniCalendar
              value={stelle.abschick_datum || ''}
              onChange={v => {
                const changes = { abschick_datum: v };
                if (v && (!status || status === 'unterlagen_angefertigt'))
                  changes.bewerbungs_status = 'abgeschickt';
                onSave(changes);
              }}
              placeholder="TT.MM.JJJJ"
            />
          </div>
          {showInterview && (
            <div style={{
              padding: '12px 13px', borderRadius: 10,
              background: 'rgba(255,188,0,0.07)',
              border: `1px solid rgba(255,188,0,0.22)`,
            }}>
              <div style={labelStyle}><CalendarSVG size={10} /> Interview-Termin</div>
              <MiniCalendar
                value={stelle.interview_datum || ''}
                onChange={v => onSave({ interview_datum: v })}
                placeholder="Datum wählen"
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 8 }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, opacity: 0.4 }}>
                  <circle cx="12" cy="12" r="10" stroke={C.textMuted} strokeWidth="1.7"/>
                  <path d="M12 6v6l4 2" stroke={C.textMuted} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <input
                  type="time"
                  defaultValue={stelle.interview_uhrzeit || ''}
                  style={{ ...inputStyle, flex: 1 }}
                  onFocus={onFocus}
                  onBlur={e => { onBlur(e); onSave({ interview_uhrzeit: e.target.value }); }}
                />
              </div>
            </div>
          )}
        </div>

        {/* ── Col 2: Gehalt ── */}
        <div>
          <div style={labelStyle}><EuroSVG size={10} /> Gehaltsvorstellung</div>
          <input
            type="text"
            defaultValue={stelle.gehaltsvorstellung || ''}
            placeholder="z.B. 55.000 €"
            style={inputStyle}
            onFocus={onFocus}
            onBlur={e => {
              onBlur(e);
              const formatted = formatGehalt(e.target.value);
              if (formatted !== e.target.value) e.target.value = formatted;
              if (formatted !== (stelle.gehaltsvorstellung || ''))
                onSave({ gehaltsvorstellung: formatted });
            }}
            onKeyDown={e => e.key === 'Enter' && e.target.blur()}
          />
        </div>

        {/* ── Col 3: Notizen ── */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ ...labelStyle, marginBottom: 8 }}>
            Notizen
            {notizen.length > 0 && (
              <span style={{
                fontSize: 10, fontWeight: 700, marginLeft: 4,
                background: C.tealMid, color: C.teal,
                padding: '1px 7px', borderRadius: 999,
              }}>{notizen.length}</span>
            )}
          </div>
          {notizen.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, maxHeight: 160, overflowY: 'auto', scrollbarWidth: 'none', marginBottom: 8 }}>
              {notizen.map(n => (
                <div key={n.id} style={{
                  background: C.surface, borderRadius: 8,
                  padding: '8px 10px', border: `1px solid ${C.border}`,
                  position: 'relative',
                }}>
                  <div style={{ fontSize: 12, color: C.text, lineHeight: 1.5, paddingRight: 18, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{n.text}</div>
                  {n.erstellt_am && (
                    <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>{formatNoteDate(n.erstellt_am)}</div>
                  )}
                  <button
                    onClick={() => removeNotiz(n.id)}
                    style={{
                      position: 'absolute', top: 6, right: 7,
                      background: 'none', border: 'none',
                      color: C.textMuted, fontSize: 14, cursor: 'pointer',
                      lineHeight: 1, padding: 2,
                    }}
                  >×</button>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              value={neueNotiz}
              onChange={e => setNeueNotiz(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addNotiz(); } }}
              placeholder="Notiz hinzufügen…"
              style={{ ...inputStyle, flex: 1, fontSize: 12 }}
              onFocus={onFocus}
              onBlur={onBlur}
            />
            <button
              onClick={addNotiz}
              style={{
                padding: '8px 12px', borderRadius: 9,
                background: C.amber, border: 'none',
                color: '#3A2700', fontSize: 14, fontWeight: 700,
                cursor: 'pointer', flexShrink: 0, lineHeight: 1,
              }}
            >+</button>
          </div>
        </div>
      </div>

      {/* ── Footer: Löschen ── */}
      <div style={{
        display: 'flex', justifyContent: 'flex-end',
        padding: '10px 24px 12px',
        borderBottom: `1px solid ${C.border}`,
        background: `rgba(0,77,64,0.01)`,
      }}>
        <button
          onClick={async () => { if (await confirm('Stelle wirklich löschen?')) onLoeschen(stelle.id); }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '6px 13px', borderRadius: 999,
            background: 'none', border: 'none',
            color: C.textMuted, fontSize: 12, fontWeight: 500,
            cursor: 'pointer', fontFamily: C.body,
            transition: 'color 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.color = '#dc2626'; }}
          onMouseLeave={e => { e.currentTarget.style.color = C.textMuted; }}
        >
          <TrashIcon size={11} color="currentColor" /> Stelle löschen
        </button>
      </div>
    </div>
  );
}

// ─── Applications Table ───────────────────────────────────────────────────────
export function ApplicationsTable({ stellen = [], markierteIds, onMarkierenToggle, onLoeschen, onExternErstellen, onStelleGeaendert }) {
  const confirm = useConfirm();
  const [search,       setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('alle');
  const [externModal,  setExternModal]  = useState(false);
  const [overrides,    setOverrides]    = useState({});
  const [expandedId,   setExpandedId]   = useState(null);

  const getOverride = (id) => overrides[id] || {};
  const getMerged   = (s)  => ({ ...s, ...getOverride(s.id) });
  const getStatus   = (s)  => (overrides[s.id]?.bewerbungs_status ?? s.bewerbungs_status) || '';

  const filtered = useMemo(() => {
    return stellen
      .filter(s => {
        const name = (s.empfaenger_unternehmen || s.name || '').toLowerCase();
        if (search && !name.includes(search.toLowerCase())) return false;
        const status = getStatus(s);
        if (statusFilter !== 'alle' && status !== statusFilter) return false;
        return true;
      })
      .sort((a, b) => new Date(b.erstellt_am || 0) - new Date(a.erstellt_am || 0));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stellen, search, statusFilter, overrides]);

  const handleSave = useCallback((id, changes) => {
    setOverrides(prev => ({ ...prev, [id]: { ...prev[id], ...changes } }));
    updateStelle(id, changes)
      .then(() => onStelleGeaendert?.(id, changes))
      .catch(err => {
        console.error('Update failed:', err);
        setOverrides(prev => {
          const next = { ...prev, [id]: { ...prev[id] } };
          Object.keys(changes).forEach(k => delete next[id][k]);
          return next;
        });
      });
  }, [onStelleGeaendert]);

  const handleStatusChange = useCallback((id, val) => {
    handleSave(id, { bewerbungs_status: val });
    if (val === 'abgeschickt') setExpandedId(id);
  }, [handleSave]);

  const handleDelete = async (id) => {
    if (!await confirm('Stelle wirklich löschen?')) return;
    try { await deleteStelle(id); onLoeschen?.(id); setExpandedId(null); } catch {}
  };

  const chips = [
    { value: 'alle',                 label: 'Alle'        },
    { value: 'abgeschickt',          label: 'Abgeschickt' },
    { value: 'kennenlerngespraech',  label: 'Interview'   },
    { value: 'zusage',               label: 'Zusage'      },
    { value: 'absage',               label: 'Absage'      },
  ];

  return (
    <div id="tutorial-an-stellen" className="an-card">
      <div className="an-card-head">
        <div>
          <div className="an-card-eyebrow">Bewerbungen</div>
          <h3 className="an-card-title">Alle Stellen</h3>
        </div>
        {onExternErstellen && (
          <button id="tutorial-an-extern" className="an-extern-add-btn" onClick={() => setExternModal(true)}>
            <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
              <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Extern hinzufügen
          </button>
        )}
      </div>

      <div className="an-tbl-toolbar">
        <div className="an-tbl-search">
          <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
            <circle cx="6" cy="6" r="4" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M9.5 9.5l2.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Unternehmen suchen…"
          />
        </div>
        <div className="an-chip-group">
          {chips.map(c => (
            <button
              key={c.value}
              className={`an-chip${statusFilter === c.value ? ' an-chip--active' : ''}`}
              onClick={() => setStatusFilter(c.value)}
            >
              {c.label}
              {c.value !== 'alle' && (
                <span className="an-chip-count">
                  {stellen.filter(s => getStatus(s) === c.value).length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="an-tbl-empty">Keine Stellen gefunden.</div>
      ) : (
        <table className="an-tbl">
          <thead>
            <tr>
              <th>Unternehmen</th>
              <th>Status</th>
              <th>Abgeschickt</th>
              <th>Gehalt</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(s => {
              const merged    = getMerged(s);
              const name      = merged.empfaenger_unternehmen || merged.name || 'Unbenannt';
              const isOpen    = expandedId === s.id;
              const ansprech  = merged.empfaenger_ansprechperson;
              const gehalt    = overrides[s.id]?.gehaltsvorstellung ?? s.gehaltsvorstellung;
              const hasChanges = overrides[s.id] && Object.keys(overrides[s.id]).length > 0;
              return (
                <React.Fragment key={s.id}>
                  <tr
                    className={`an-tbl-row${isOpen ? ' an-tbl-row--expanded' : ''}${hasChanges && !isOpen ? ' an-tbl-row--changed' : ''}`}
                    onClick={() => setExpandedId(isOpen ? null : s.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <div className="an-tbl-company">
                        <LogoCell name={name} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                            <div className="an-tbl-name">{name}</div>
                            {hasChanges && !isOpen && (
                              <span style={{
                                fontSize: 10, fontWeight: 700,
                                color: '#7A5000',
                                background: 'rgba(255,188,0,0.22)',
                                border: '1px solid rgba(255,188,0,0.45)',
                                padding: '1px 7px', borderRadius: 999,
                                letterSpacing: '0.04em',
                                whiteSpace: 'nowrap',
                              }}>Gespeichert ✓</span>
                            )}
                          </div>
                          {ansprech && <div className="an-tbl-sub">{ansprech}</div>}
                        </div>
                      </div>
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <StatusCell stelle={{ ...merged, bewerbungs_status: getStatus(s) }} onStatusChange={handleStatusChange} />
                    </td>
                    <td className="an-tbl-muted">
                      <DateCell
                        value={overrides[s.id]?.abschick_datum ?? s.abschick_datum}
                        onSave={v => handleSave(s.id, { abschick_datum: v })}
                      />
                    </td>
                    <td className="an-tbl-muted">{gehalt || '—'}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className="an-row-actions">
                        <button
                          className={`an-action-btn${markierteIds?.has(s.id) ? ' an-action-btn--active' : ''}`}
                          onClick={() => onMarkierenToggle?.(s.id)}
                          title="Markieren"
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                            <path d="M9 22H15C20 22 22 20 22 15V9C22 4 20 2 15 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M17 2.43945V12.4195C17 14.3895 15.59 15.1595 13.86 14.1195L12.54 13.3295C12.24 13.1495 11.76 13.1495 11.46 13.3295L10.14 14.1195C8.41 15.1495 7 14.3895 7 12.4195V2.43945" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                        <button
                          className={`an-expand-btn${isOpen ? ' an-expand-btn--open' : ''}`}
                          title={isOpen ? 'Schließen' : 'Details'}
                          onClick={() => setExpandedId(isOpen ? null : s.id)}
                        >
                          <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                            <path d="M3 4.5l3 3 3-3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="an-tbl-detail-row">
                      <td colSpan={5} style={{ padding: 0 }}>
                        <InlineDetail
                          stelle={merged}
                          onSave={(changes) => handleSave(s.id, changes)}
                          onLoeschen={handleDelete}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      {externModal && (
        <ExternModal
          onSchliessen={() => setExternModal(false)}
          onErstellen={onExternErstellen}
        />
      )}
    </div>
  );
}

// ─── Sources Breakdown ────────────────────────────────────────────────────────
export function SourcesBreakdown({ stellen = [], demoSources = null }) {
  const sources = useMemo(() => demoSources ?? computeSources(stellen), [stellen, demoSources]);
  const max = Math.max(...sources.map(s => s.value), 1);
  return (
    <div className="an-card">
      <div className="an-card-head">
        <div>
          <div className="an-card-eyebrow">Quellen</div>
          <h3 className="an-card-title">Wo deine Bewerbungen herkommen</h3>
        </div>
      </div>
      <div className="an-brk-list">
        {sources.map((s, i) => (
          <div key={s.name} className="an-brk-row">
            <span className="an-brk-name">{s.name}</span>
            <div className="an-brk-bar">
              <div className={`an-brk-fill${i === 0 ? ' an-brk-fill--amber' : ''}`}
                   style={{ width: `${(s.value / max) * 100}%` }} />
            </div>
            <span className="an-brk-num">{s.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Feed SVG icons ───────────────────────────────────────────────────────────
const CheckCircleIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);
const FeedUsersIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);
const FeedSendIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
);
const XCircleIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
  </svg>
);
const FileIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
  </svg>
);

const KIND_ICON = {
  offer:  { IconComp: CheckCircleIcon, cls: 'an-feed-icon--offer'  },
  int:    { IconComp: FeedUsersIcon,   cls: 'an-feed-icon--int'    },
  sent:   { IconComp: FeedSendIcon,    cls: 'an-feed-icon--sent'   },
  reject: { IconComp: XCircleIcon,     cls: 'an-feed-icon--reject' },
  draft:  { IconComp: FileIcon,        cls: 'an-feed-icon--draft'  },
};

export function ActivityFeed({ stellen = [] }) {
  const items = useMemo(() => computeActivityFeed(stellen), [stellen]);
  return (
    <div className="an-card">
      <div className="an-card-head">
        <div>
          <div className="an-card-eyebrow">Aktivität</div>
          <h3 className="an-card-title">Letzte Ereignisse</h3>
        </div>
      </div>
      {items.length === 0 ? (
        <div className="an-feed-empty">Noch keine Aktivitäten.</div>
      ) : (
        <div className="an-feed">
          {items.map((item) => {
            const { IconComp, cls } = KIND_ICON[item.kind] || KIND_ICON.sent;
            const itemKey = `${item.company}-${item.time}`;
            return (
              <div key={itemKey} className="an-feed-item">
                <div className={`an-feed-icon ${cls}`}><IconComp /></div>
                <div className="an-feed-body">
                  <div className="an-feed-text">
                    <strong>{item.company}</strong> — {item.text}
                  </div>
                  <div className="an-feed-time">{item.time}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
